"use client"

import { useEffect, useRef } from "react"

import { contentAnalytics, type AffiliatePlacement, type EventProperties } from "@/lib/analytics"
import type { ProductRecommendation } from "@/data/guide-product-recommendations"
import type { DirectusProduct } from "@/lib/directus"
import { AffiliatePicksCard } from "@/components/tools/affiliate-picks-card"
import { extractAsin } from "@/lib/product-picks/resolve"
import { withAmazonTag } from "@/lib/affiliate"

/** Matches useAffiliateImpression, so the two denominators count the same way. */
const PRODUCT_IMPRESSION_THRESHOLD = 0.5

interface GuideProductCardProps {
  guideSlug: string
  products: ProductRecommendation[]
  /** Analytics placement. Defaults to 'inline' so existing per-section cards are unchanged. */
  placement?: AffiliatePlacement
  /** Card heading. Defaults to "Recommended Products". */
  title?: string
  /** Unified-telemetry surface. Defaults to `guide-<slug>` so the in-body picks
   *  keep their historical key; the sidebar experiment passes its own. */
  surface?: string
  /** Extra properties stamped on the surface impression and its clicks. */
  eventProps?: EventProperties
  /** Stacked rows for narrow containers (the guide rail). */
  stacked?: boolean
  /** Section anchor id this block sits in ('early' for the ungated hoisted card).
   *  Passing it turns on the per-product `denominator: 'product'` impressions
   *  alongside the container impression, and stamps `section_id` on the clicks. */
  sectionId?: string
  /** Which content family the block rendered in. Additive; omitted where the
   *  card is reused outside /guides (weed and pest pages). */
  railHost?: "blog" | "guide"
}

/**
 * Guide product roundup. Thin adapter over the shared AffiliatePicksCard (the
 * "Affiliate CTA — 3 items" design) so guide sections render the same premium
 * card as the tool/city surfaces — serif header, ranked rows, "Check price"
 * CTAs — instead of the old plain list.
 *
 * The static ProductRecommendation shape maps straight on: `imageUrl` -> `image`
 * (getDirectusImageUrl passes http URLs through untouched) and `affiliateUrl` ->
 * `affiliate_url`. The card owns layout, disclosure, and ranking; the eyebrow is
 * suppressed so each callsite's own title (e.g. "What you need for a Bermuda
 * lawn") stays the header without a redundant "Our picks" kicker.
 */
export function GuideProductCard({
  guideSlug,
  products,
  placement = "inline",
  title = "Recommended Products",
  surface,
  eventProps,
  stacked,
  sectionId,
  railHost,
}: GuideProductCardProps) {
  const resolvedSurface = surface ?? `guide-${guideSlug}`
  const blockRef = useRef<HTMLDivElement | null>(null)
  const firedRef = useRef<Set<number>>(new Set())

  // Per-product impressions, emitted ALONGSIDE the card's own container
  // impression so `denominator: 'container'` and `denominator: 'product'`
  // series stay separable and the container series keeps its history.
  //
  // The rows are observed by querying AffiliatePicksCard's rendered list rather
  // than by threading refs through it: that card is shared with the blog picks
  // rail and the tool pages, which count one impression per block and must not
  // change. It renders exactly one <li> per product, in order, so the row index
  // is the product index.
  useEffect(() => {
    if (!sectionId) return
    const root = blockRef.current
    if (!root) return
    const rows = Array.from(root.querySelectorAll<HTMLElement>("ul > li"))
    if (rows.length === 0) return

    const fired = firedRef.current
    const emit = (index: number) => {
      if (fired.has(index)) return
      const product = products[index]
      if (!product) return
      fired.add(index)
      contentAnalytics.affiliateProductShown(resolvedSurface, {
        denominator: "product",
        asin: extractAsin(product.affiliateUrl) ?? undefined,
        product_name: product.name,
        affiliate_link: withAmazonTag(product.affiliateUrl, "card"),
        article_slug: guideSlug,
        section_id: sectionId,
        position: index + 1,
        ...(railHost ? { rail_host: railHost } : {}),
      })
    }

    if (typeof IntersectionObserver === "undefined") {
      rows.forEach((_, index) => emit(index))
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const index = rows.indexOf(entry.target as HTMLElement)
          if (index >= 0) emit(index)
          observer.unobserve(entry.target)
        }
      },
      { threshold: PRODUCT_IMPRESSION_THRESHOLD },
    )
    rows.forEach((row) => observer.observe(row))
    return () => observer.disconnect()
  }, [products, resolvedSurface, sectionId, guideSlug, railHost])

  if (products.length === 0) return null

  const items = products.map((p, i) => ({
    id: `${guideSlug}-${i}`,
    name: p.name,
    description: p.description,
    image: p.imageUrl ?? "",
    affiliate_url: withAmazonTag(p.affiliateUrl, "card"),
  })) as unknown as DirectusProduct[]

  const seeAllHref = `/products?category=${encodeURIComponent(
    products[0]?.productsPageFilter || "",
  )}&source=${encodeURIComponent(`guide-inline-${guideSlug}`)}`

  const cardEventProps: EventProperties = {
    ...eventProps,
    ...(sectionId ? { section_id: sectionId } : {}),
    ...(railHost ? { rail_host: railHost } : {}),
  }

  return (
    <div className="my-6" ref={blockRef}>
      <AffiliatePicksCard
        title={title}
        eyebrow=""
        products={items}
        seeAllHref={seeAllHref}
        surface={resolvedSurface}
        eventProps={cardEventProps}
        placement={placement}
        stacked={stacked}
        onProductClick={(dp) =>
          contentAnalytics.affiliateLinkClicked(guideSlug, dp.name, dp.affiliate_url, placement)
        }
      />
    </div>
  )
}
